<script lang="ts">import Link from '../../../shared/Link/Link.svelte';
import CloseButton from '../../../shared/CloseButton/CloseButton.svelte';
import LinkList from '../../../shared/LinkList/LinkList.svelte';
import BackButton from '../../../shared/BackButton/BackButton.svelte';
import { getComponentType } from '../../../../../js-v2/componentUtils';
import { mobileCtaSubnavMenuStateStore } from '../../../../../js-v2/stores/mobile-cta-subnav-stores';
import { getTracking } from '../../../../../js-v2/tracking';
import { getLocation, getNavDeviceType } from '../../../../../js-v2/trackingUtils';
import { getUrlPath } from '../../../../../js-v2/urlUtils';
import { mobileNavStateStore } from '../../../../../js-v2/stores/mobile-nav-stores';
import { ComponentType, MENU_TOGGLE_SELECTOR } from '../../../../../js-v2/constants';
const { navItems = [], mobileNavWidth = 0 } = $props();
const trackFn = getTracking();
let clientHeight = $state(0);
let dropdownEl = $state();
const linkItems = $derived(navItems.filter((item) => getComponentType(item.componentName) === 'link'));
const categoryItems = $derived(navItems.filter((item) => getComponentType(item.componentName) === 'category' && getCategoryProps(item)));
function closeButtonClickTouchFn() {
    mobileCtaSubnavMenuStateStore.toggle();
    trackFn({
        eventName: 'Close Menu',
        properties: {
            currentPage: getUrlPath(),
            isCloseButton: true,
            menuLocation: getLocation(dropdownEl),
            navDeviceType: getNavDeviceType(dropdownEl),
        },
    });
}
function openGlobalNav() {
    mobileNavStateStore.toggle({ openFromSubnav: true });
    trackFn({
        eventName: 'Open Drawer',
        properties: {
            currentPage: getUrlPath(),
            menuLocation: ComponentType.MAIN_NAVIGATION,
            navDeviceType: getNavDeviceType(dropdownEl),
        },
    });
}
function backToGlobalNav() {
    closeButtonClickTouchFn();
    openGlobalNav();
    // manually send focus to the menu toggle button
    // because the global nav is located above this component, the focus is not automatically sent to it
    document.querySelector(MENU_TOGGLE_SELECTOR)?.focus();
}
function getCategoryProps(navItem) {
    return 'primaryNavItems' in navItem.props ? navItem.props : null;
}
</script>

{#if $mobileCtaSubnavMenuStateStore.isOpen || $mobileNavStateStore.isOpen}
  <div class="transition-shim" aria-hidden="true"></div>
{/if}

<div bind:this={dropdownEl} class="Dropdown" style:height={`${clientHeight}px`}>
  <div bind:clientHeight>
    <div class="header" class:active={$mobileCtaSubnavMenuStateStore.isOpen} style:width={`${mobileNavWidth}px`}>
      <div class="left-slot">
        <BackButton
          isActive={$mobileCtaSubnavMenuStateStore.isOpen}
          clickTouchFn={backToGlobalNav}
          text="Go to main menu"
        />
      </div>
      <div class="right-slot">
        <CloseButton isActive={$mobileCtaSubnavMenuStateStore.isOpen} clickTouchFn={closeButtonClickTouchFn} />
      </div>
    </div>
    <div class="dropdown-nav-item-container slide-in-from-top" class:active={$mobileCtaSubnavMenuStateStore.isOpen}>
      <ul>
        {#each linkItems as navItem, index}
          <li style:--dropdown-item-number={index + 1}>
            <Link
              linkCustomCssClass="subcategory-large-link"
              id={navItem.contentfulEntryId}
              title={navItem.props.title}
              url={navItem.props.url}
              linkStyle={navItem.props.linkStyle}
              openInNewTab={navItem.props.openInNewTab}
            />
          </li>
        {/each}
        {#each categoryItems as navItem}
          <div class="category-item-list">
            <LinkList
              id={navItem.contentfulEntryId}
              title={navItem.props.title}
              navItems={getCategoryProps(navItem)?.primaryNavItems}
              linkStartingIndex={linkItems.length}
            />
          </div>
        {/each}
      </ul>
    </div>
  </div>
</div>

<style>@keyframes fade-in-slide-from-top {
  from {
    opacity: 0;
    transform: translateX(0) translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes fade-in-slide-from-left {
  from {
    opacity: 0;
    transform: translateX(-4px) translateY(0);
  }
  to {
    opacity: 1;
    transform: translateX(0) translateY(0);
  }
}
@keyframes slide-down {
  from {
    transform: translateY(-50%);
  }
  to {
    transform: translateY(0);
  }
}
/**
We use rem units for media queries to promote accessible layouts. By utilizing rem units, the computed values in media queries are determined by the user's browser-defined font size rather than our global 'font-size: 62.5%' reset. This approach ensures that our design adapts to individual user settings, enhancing accessibility. 
*/
.transition-shim {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
  z-index: 2;
}

.Dropdown {
  z-index: 4;
  position: fixed;
  left: 0;
  top: 0;
  width: 100vw;
  min-width: 320px;
  background-color: var(--color-background, var(--color-neutral-1000, #fff));
  overflow: hidden;
  transition: height 300ms cubic-bezier(0.4, 0, 0.6, 1);
}
.Dropdown .header {
  display: none;
  overflow: auto;
  height: 72px;
  padding: 0px 22px;
}
.Dropdown .header.active {
  display: flex;
}
.Dropdown .header .left-slot,
.Dropdown .header .right-slot {
  display: flex;
  align-items: center;
  width: 100%;
  flex: 0 1 auto;
}
.Dropdown .header .left-slot {
  justify-content: flex-start;
}
.Dropdown .header .left-slot :global(.BackButton) {
  display: block;
}
@media (min-width: 64rem) {
  .Dropdown .header .left-slot :global(.BackButton) {
    display: none;
  }
}
.Dropdown .header .right-slot {
  justify-content: flex-end;
}
.Dropdown .dropdown-nav-item-container {
  display: none;
  overflow: auto;
  padding: var(--accessibility-focus-outline-offset) 32px 150px;
}
.Dropdown .dropdown-nav-item-container .category-item-list {
  margin-top: 35px;
}
.Dropdown .dropdown-nav-item-container.slide-in-from-top li {
  opacity: 0;
  transform: translateX(0) translateY(-4px);
}
.Dropdown .dropdown-nav-item-container.slide-in-from-top :global(.LinkList .slide-in-from-top) {
  opacity: 0;
  transform: translateX(0) translateY(-4px);
}
.Dropdown .dropdown-nav-item-container.active {
  display: flex;
  flex-direction: column;
  gap: 35px;
  height: calc(100vh - 72px);
}
.Dropdown .dropdown-nav-item-container.active.slide-in-from-top li {
  animation: fade-in-slide-from-top 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--dropdown-item-number) * 20ms);
}
.Dropdown .dropdown-nav-item-container.active.slide-in-from-top :global(.LinkList .slide-in-from-top) {
  animation: fade-in-slide-from-top 300ms ease-in forwards;
  animation-delay: calc(150ms + var(--link-list-item-number) * 20ms);
}
.Dropdown .dropdown-nav-item-container ul {
  display: block;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  list-style-type: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.Dropdown .dropdown-nav-item-container ul > li {
  display: block;
  position: static;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
.Dropdown .dropdown-nav-item-container ul > li::before {
  display: none;
}
.Dropdown .dropdown-nav-item-container ul > li :global(:last-child) {
  margin: 0;
}</style>
